import { type CatalogueProductView, type SubscriptionTier } from '../contracts';

/** Share of a plan's credit limit a member can use and still be suggested that plan. */
const SUGGESTED_TIER_USAGE_RATIO = 0.75;

/** The product tier to suggest within: Clinician for individuals, Practice for teams. */
export type SuggestableProductTier =
  | SubscriptionTier.CLINICIAN_PRO
  | SubscriptionTier.CLINICIAN_PRO_PLUS;

type SuggestableProduct = Pick<CatalogueProductView, 'new_product_tier' | 'credit_limit'>;

export type SuggestTierInput<TProduct extends SuggestableProduct> = {
  /** Credits consumed over a monthly cycle, as the API sends them. */
  consumed: string;
  products: readonly TProduct[];
  newProductTier: SuggestableProductTier;
};

const toCreditAmount = (value: string): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Among products of `newProductTier`, the smallest plan whose credit limit the
 * member used under 75% of, so a member past 75% of one plan is suggested the
 * next. Falls back to the biggest plan, and null when none has a credit limit.
 */
export const suggestTier = <TProduct extends SuggestableProduct>({
  consumed,
  products,
  newProductTier,
}: SuggestTierInput<TProduct>): TProduct | null => {
  const limitOf = (product: TProduct) => toCreditAmount(product.credit_limit ?? '');
  const candidates = products.filter(
    (product) => product.new_product_tier === newProductTier && limitOf(product) > 0
  );
  // Stable sort, so catalogue order breaks a tie between equal limits.
  candidates.sort((a, b) => limitOf(a) - limitOf(b));
  const [smallest, ...rest] = candidates;
  if (smallest == null) return null;
  // Strict >, so the first of several plans tied at the biggest limit wins.
  const biggest = rest.reduce(
    (top, product) => (limitOf(product) > limitOf(top) ? product : top),
    smallest
  );
  const used = toCreditAmount(consumed);

  return (
    candidates.find((product) => used < limitOf(product) * SUGGESTED_TIER_USAGE_RATIO) ??
    biggest
  );
};
